import React, { useMemo } from 'react'
import moment from 'moment'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import useToggle from 'hooks/useToggle'
import { INTL_PICKER_DATE_FORMAT, INTL_DAY_MONTH_NAME_SHORT, INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, ONE_DAY_BEFORE_CHECK_IN, TWO_DAYS_BEFORE_CHECK_IN, THREE_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, SEVEN_DAYS_BEFORE_CHECK_IN, THIRTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN, FORTY_FIVE_DAYS_BEFORE_CHECK_IN, SIXTY_DAYS_BEFORE_CHECK_IN, NINETY_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy;
  checkInDate?: string | moment.Moment;
  className?: string;
  propertyTimezoneOffset?: number;
  hideNonRefundableMessage?: boolean;
  textLinkFormat?: boolean
}

function AlwaysOnOfferPropertyCancellationPolicies(props: Props) {
  const {
    cancellationPolicy,
    checkInDate,
    className,
    propertyTimezoneOffset,
    hideNonRefundableMessage,
    textLinkFormat,
  } = props
  const [isPolicyOpen, togglePolicy] = useToggle()
  const intlDateFormatter = useIntlDateFormatter()

  const standardDetail = 'Standard bookings can be cancelled with a full refund provided the cancellation is made until check-in. That is cancel by 11:59 pm (local time of property) prior to the day of check-in.'
  const nonRefundableDetail = 'Please note that ‘Non-Refundable’ packages are guaranteed at the time of booking and not eligible for a refund or date change. Accordingly, please select your package carefully and take note of any cancellation policy applicable to your purchase.'
  let cancellationPolicyDetail = cancellationPolicy.policyDetail[1] ?? standardDetail

  const days = useMemo(() => {
    switch (cancellationPolicy.type) {
      case 'prior-to-check-in-one-day':
        return ONE_DAY_BEFORE_CHECK_IN
      case 'prior-to-check-in-two-days':
        return TWO_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-three-days':
        return THREE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-seven-days':
        return SEVEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-fourteen-days':
        return FOURTEEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-one-days':
      case 'post-purchase-seven-days':
        return TWENTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-eight-days':
        return TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-thirty-one-days':
        return THIRTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-forty-five-days':
        return FORTY_FIVE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-sixty-days':
        return SIXTY_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-ninety-days':
        return NINETY_DAYS_BEFORE_CHECK_IN
      default:
        return 0
    }
  }, [cancellationPolicy.type])

  let startDate: moment.Moment | undefined

  const startBasedOnCheckIn = checkInDate ? ((propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))) : undefined
  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicy.type === 'post-purchase-seven-days') {
    if (!startBasedOnCheckIn) {
      startDate = startBasedOnToday
    } else {
      startDate = startBasedOnToday.isBefore(startBasedOnCheckIn) ? startBasedOnToday : startBasedOnCheckIn
    }
  } else {
    startDate = startBasedOnCheckIn
  }

  const refundable = cancellationPolicy.type !== 'non-refundable' && (!startDate || startDate?.isAfter(moment()))
  if (cancellationPolicy.type !== 'non-refundable' && !refundable) {
    cancellationPolicyDetail = nonRefundableDetail
  }

  const onClickIcon: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    togglePolicy()
  }

  return <>
    <Clickable onClick={onClickIcon} className={className}>
      {!hideNonRefundableMessage && <>
        {refundable && <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-secondary"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              Free cancellation
            </TextLink>}
          {!textLinkFormat && <>
            <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">Free cancellation</BodyText>{' '}
            {!startDate && Boolean(days) && <BodyText variant="medium" as="span">{days} days prior to check in (Hotel only)</BodyText>}
            {startDate && <BodyText variant="medium" as="span">until {intlDateFormatter(startDate, INTL_SHORT_DAY_NAME_DAY_MONTH)} (Hotel only)</BodyText>}
            {' '}<MiddledTooltipIcon size="XS" />

          </>}
        </>}
        {!refundable && cancellationPolicy?.type !== 'hidden-cancellation-policy' && <>
          <BodyText variant="medium" as="span">Non-refundable</BodyText>
          {' '}<MiddledTooltipIcon size="XS" />
        </>}
      </>}
      {hideNonRefundableMessage && refundable && <>
        {textLinkFormat &&
          <TextLink
            size="medium"
            colour="highlight-secondary"
            underline={false}
            startIcon={<LineRefundIcon />}
          >
            Free cancellation available
          </TextLink>}
        {!textLinkFormat && <>
          <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">Free cancellation available</BodyText>{' '}
          {startDate &&
            <BodyText variant="medium" as="span">until {intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT)}</BodyText>}
          {' '}<MiddledTooltipIcon size="XS" />
        </>}
      </>}
    </Clickable>
    <Modal
      title={refundable ? 'Free cancellation' : 'Non refundable'}
      isOpen={isPolicyOpen}
      onClose={onClickIcon}
    >
      {(!startDate || !refundable) && (
        <BodyText variant="large" as="p">{cancellationPolicyDetail}</BodyText>
      )}
      {refundable && startDate && (
        <BodyText variant="large" as="p">Standard bookings can be cancelled with a full refund provided the cancellation made by {intlDateFormatter(startDate, INTL_PICKER_DATE_FORMAT)} 11:59 PM (local time of property).</BodyText>
      )}
      <HelpPane />
    </Modal>
  </>
}

export default AlwaysOnOfferPropertyCancellationPolicies
